import React, { FC } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Chip from '@mui/material/Chip';
import { formatPipelineTimestamp } from 'utils/formatTimestamp';
import { formatDuration } from 'utils/formatters/orderPipelineAudit';
import { PipelineRun, PipelineRunOutcome } from 'api/admin/orders/getOrderPipelineAudit';
import { NOT_RECORDED, OUTCOME_DISPLAY } from 'constants/order/orderPipelineAudit';
import { ChangeRow } from './ChangeRow';
import { PipelineStepItem } from './PipelineStepItem';
import styles from './OrderPipelineHistory.module.scss';

interface PipelineRunItemProps {
  run: PipelineRun;
}

/** One reconstructed pipeline run: its outcome, its snapshot diff, and the UoW events it recorded. */
export const PipelineRunItem: FC<PipelineRunItemProps> = ({ run }) => {
  const outcome = OUTCOME_DISPLAY[run.outcome] ?? OUTCOME_DISPLAY[PipelineRunOutcome.INCOMPLETE];

  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <div className={styles.runSummary}>
          <span className={styles.runTimestamp}>{formatPipelineTimestamp(run.startedAt)}</span>
          <span className={styles.runPipeline}>{run.pipeline ?? 'Unknown pipeline'}</span>

          <Chip size="small" label={outcome.label} color={outcome.color} />

          <span className={styles.runMeta}>
            {run.origin ?? NOT_RECORDED} · {formatDuration(run.durationMs)}
          </span>
        </div>
      </AccordionSummary>

      <AccordionDetails>
        {run.error && (
          <div className={styles.section}>
            <p className={styles.sectionTitle}>Error</p>
            <div className={styles.error}>{run.error}</div>
          </div>
        )}

        <div className={styles.section}>
          <p className={styles.sectionTitle}>Changes</p>
          {run.changes.length ? (
            run.changes.map((change) => <ChangeRow key={change.field} change={change} />)
          ) : (
            <span className={styles.runMeta}>
              No before/after comparison recorded for this run.
            </span>
          )}
        </div>

        <div className={styles.section}>
          <p className={styles.sectionTitle}>Steps</p>
          {run.steps.length ? (
            run.steps.map((step) => (
              <PipelineStepItem key={`${step.uow}-${step.at}-${step.event}`} step={step} />
            ))
          ) : (
            <span className={styles.runMeta}>No unit-of-work events recorded for this run.</span>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
